import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import FilterModal from "../components/FilterModal";
import WorkCard from "../components/WorkCard";
import BottomNavbar from "../components/BottomNavbar";
import Header from "../components/Header";
import "./css/Dashboard.css";

import profileIncompleteImage from "../assets/images/profile_incomplete.png";
import noWorkImage from "../assets/images/no_work.png";

export default function Dashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingWorks, setLoadingWorks] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [filterData, setFilterData] = useState(null);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [sponsoredAds, setSponsoredAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const adIntervalRef = useRef(null);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;
  const LIMIT = 20;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      hi: "Hi",
      filter: "Filter",
      profileCompletion: "Profile Completion",
      basicDetails: "Basic Details",
      pastWorks: "Past Works",
      workPreferences: "Work Preferences",
      completed: "Completed",
      pending: "Pending",
      completeProfile: "Complete profile",
      editProfile: "Edit profile",
      findingWorks: "We are finding works that matches you, please check back later!",
      completeProfileToStart: "Complete your profile to start getting works.",
      noWorkAvailable: "No work available",
      availableWorks: "Available Works",
      sponsoredAd: "Sponsored Ad",
    },
    ar: {
      loading: "جاري التحميل",
      hi: "مرحباً",
      filter: "تصفية",
      profileCompletion: "اكتمال الملف الشخصي",
      basicDetails: "التفاصيل الأساسية",
      pastWorks: "الأعمال السابقة",
      workPreferences: "تفضيلات العمل",
      completed: "مكتمل",
      pending: "معلق",
      completeProfile: "إكمال الملف الشخصي",
      editProfile: "تعديل الملف الشخصي",
      findingWorks: "نحن نبحث عن أعمال تناسبك، يرجى التحقق مرة أخرى لاحقاً!",
      completeProfileToStart: "أكمل ملفك الشخصي للبدء في الحصول على الأعمال.",
      noWorkAvailable: "لا توجد أعمال متاحة",
      availableWorks: "الأعمال المتاحة",
      sponsoredAd: "إعلان مدعوم",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;

          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);

          // Check if profile_completed_step is not 5, navigate to completion step
          const completedStep = profileData.profile_completed_step || 0;
          if (completedStep !== 5) {
            const nextStep = completedStep + 1;
            navigate(`/profile-step-${nextStep}`);
            return;
          }

          // If profile is complete and all sections are done, fetch works
          if (
            profileData.general_profile_completed &&
            profileData.past_works_completed &&
            profileData.work_preferences_completed
          ) {
            // Fetch works after a small delay to ensure state is updated
            setTimeout(() => {
              fetchWorks(1, null);
            }, 100);
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        if (err.code !== 404) {
          showToast(err.message || "Failed to load profile", "error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USER_BASE_URL, VERSION]);

  // Fetch sponsored ads when profile is complete
  useEffect(() => {
    const fetchSponsoredAds = async () => {
      if (!profile) return;
      
      const isProfileComplete =
        profile.general_profile_completed &&
        profile.past_works_completed &&
        profile.work_preferences_completed;

      if (!isProfileComplete) {
        setSponsoredAds([]);
        return;
      }

      try {
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/app-details`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const ads = data.response.contractor_ads || [];
          setSponsoredAds(ads);
          if (ads.length > 0) {
            setCurrentAdIndex(0);
          }
        }
      } catch (err) {
        console.error("Error fetching sponsored ads:", err);
        // Silently fail - ads are optional
        setSponsoredAds([]);
      }
    };

    fetchSponsoredAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, USER_BASE_URL, VERSION]);

  // Auto-play slideshow for sponsored ads
  useEffect(() => {
    if (sponsoredAds.length <= 1) {
      if (adIntervalRef.current) {
        clearInterval(adIntervalRef.current);
        adIntervalRef.current = null;
      }
      return;
    }

    // Clear any existing interval
    if (adIntervalRef.current) {
      clearInterval(adIntervalRef.current);
    }

    // Set up auto-play (change ad every 5 seconds)
    adIntervalRef.current = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % sponsoredAds.length);
    }, 5000);

    return () => {
      if (adIntervalRef.current) {
        clearInterval(adIntervalRef.current);
      }
    };
  }, [sponsoredAds.length]);

  // Fetch works
  const fetchWorks = async (page = 1, filterPayload = null) => {
    try {
      setLoadingWorks(true);
      let url = `${USER_BASE_URL}/${VERSION}/work/fetch/?limit=${LIMIT}&page=${page}`;
      
      let response;
      // POST with filters if filterPayload has sub_categories
      if (filterPayload && filterPayload.sub_categories && filterPayload.sub_categories.length > 0) {
        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
          body: JSON.stringify(filterPayload),
        });
      } else {
        // GET without filters
        response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0"
          },
          body: null,
        });
      }

      const data = await response.json();

      if (data && data.response_code === 200) {
        if (page === 1) {
          setWorks(data.response || []);
        } else {
          setWorks((prev) => [...prev, ...(data.response || [])]);
        }
        setHasMorePages((data.response || []).length === LIMIT);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Error fetching works:", err);
      showToast(err.message || "Failed to load works", "error");
    } finally {
      setLoadingWorks(false);
    }
  };

  // Handle filter apply
  const handleFilterApply = (filterPayload) => {
    setFilterData(filterPayload);
    setCurrentPage(1);
    fetchWorks(1, filterPayload);
    setShowFilter(false);
  };

  // Infinite scroll observer ref
  const observerTarget = useRef(null);

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingWorks && hasMorePages) {
      fetchWorks(currentPage + 1, filterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingWorks, hasMorePages, currentPage, filterData]);

  // Infinite scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loadingWorks) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore, hasMorePages, loadingWorks]);

  // Calculate profile completion percentage
  const calculateCompletionPercentage = () => {
    if (!profile) return 0;
    let completed = 0;
    if (profile.general_profile_completed) completed++;
    if (profile.past_works_completed) completed++;
    if (profile.work_preferences_completed) completed++;
    return Math.round((completed / 3) * 100);
  };

  // Get completion status color
  const getCompletionColor = () => {
    const percentage = calculateCompletionPercentage();
    if (percentage === 100) return "#10b981"; // Green
    if (percentage >= 50) return "#f59e0b"; // Orange
    return "#EC1C24"; // Red
  };

  // Loading screen
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const completionPercentage = calculateCompletionPercentage();
  const isProfileComplete =
    profile.general_profile_completed &&
    profile.past_works_completed &&
    profile.work_preferences_completed;

  // Get the next incomplete section to redirect to
  const getNextIncompleteSection = () => {
    if (!profile.general_profile_completed) {
      return "/profile";
    }
    if (!profile.past_works_completed) {
      return "/past-works";
    }
    if (!profile.work_preferences_completed) {
      return "/work-preferences";
    }
    return "/profile"; // Default to profile if all are complete
  };

  return (
    <div className="dashboard-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Profile Completion Card */}
      {!isProfileComplete && (
        <div className="profile-completion-card">
          <p className="title">{t.hi}, {profile.name || "User"} 👋</p>
          <div className="completion-header">
            <h3 className="completion-title">{t.profileCompletion}</h3>
            {completionPercentage === 100 && (
              <span className="completion-percentage">{completionPercentage}%</span>
            )}
          </div>
          <div className="completion-progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${completionPercentage}%`,
                backgroundColor: getCompletionColor(),
              }}
            ></div>
          </div>
          <div className="completion-items">
            <div className="completion-item">
              {profile.general_profile_completed ? (
                <i className="bx bx-check-circle completed-icon"></i>
              ) : (
                <i className="bx bx-radio-circle pending-icon"></i>
              )}
              <span>
                {profile.general_profile_completed
                  ? `${t.basicDetails} ${t.completed}`
                  : `${t.basicDetails} ${t.pending}`}
              </span>
            </div>
            <div className="completion-item">
              {profile.past_works_completed ? (
                <i className="bx bx-check-circle completed-icon"></i>
              ) : (
                <i className="bx bx-radio-circle pending-icon"></i>
              )}
              <span>
                {profile.past_works_completed
                  ? `${t.pastWorks} ${t.completed}`
                  : `${t.pastWorks} ${t.pending}`}
              </span>
            </div>
            <div className="completion-item">
              {profile.work_preferences_completed ? (
                <i className="bx bx-check-circle completed-icon"></i>
              ) : (
                <i className="bx bx-radio-circle pending-icon"></i>
              )}
              <span>
                {profile.work_preferences_completed
                  ? `${t.workPreferences} ${t.completed}`
                  : `${t.workPreferences} ${t.pending}`}
              </span>
            </div>
          </div>
          <div className="completion-footer">
            {completionPercentage === 100 ? (
              <button
                onClick={() => navigate("/profile")}
                className="complete-profile-link"
              >
                {t.editProfile} &gt;
              </button>
            ) : (
              <button
                onClick={() => navigate(getNextIncompleteSection())}
                className="complete-profile-link"
              >
                {t.completeProfile} &gt;
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filter Button */}
      {isProfileComplete && (
        <div className="filter-btn-container">
          <p className="title">{t.hi}, {profile.name || "User"} 👋</p>
          <button
            className="filter-btn-icon"
            onClick={() => setShowFilter(true)}
            title={t.filter}
          >
            <i className="bx bx-filter-alt"></i>
          </button>
        </div>
      )}

      {/* Sponsored Ads Section */}
      {isProfileComplete && sponsoredAds.length > 0 && (
        <div className="sponsored-ads-section">
          <h3 className="sponsored-ads-title">{t.sponsoredAd}</h3>
          <div className="sponsored-ads-container">
            {sponsoredAds.map((ad, index) => {
              // Handle different _id formats (object with $oid or string)
              const adId = ad._id?.$oid || (typeof ad._id === 'string' ? ad._id : null) || ad.id || index;
              return (
                <a
                  key={adId}
                  href={ad.redirect_url || "#"}
                  target={ad.redirect_url && ad.redirect_url !== "#" ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  className={`sponsored-ad-item ${index === currentAdIndex ? "active" : ""}`}
                  onClick={(e) => {
                    if (!ad.redirect_url || ad.redirect_url === "#") {
                      e.preventDefault();
                    }
                  }}
                >
                  <img
                    src={ad.url}
                    alt="Sponsored Ad"
                    className="sponsored-ad-image"
                  />
                </a>
              );
            })}
            {/* Dots indicator */}
            {sponsoredAds.length > 1 && (
              <div className="sponsored-ads-dots">
                {sponsoredAds.map((_, index) => (
                  <button
                    key={index}
                    className={`sponsored-ad-dot ${index === currentAdIndex ? "active" : ""}`}
                    onClick={() => setCurrentAdIndex(index)}
                    aria-label={`Go to ad ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="dashboard-content">
        {!isProfileComplete ? (
          // Profile not complete - show illustration
          <div className="no-content-container">
            <img
              src={profileIncompleteImage}
              alt="Profile Incomplete"
              className="no-content-image"
            />
            <p className="no-content-message">{t.completeProfileToStart}</p>
          </div>
        ) : loadingWorks && works.length === 0 ? (
          // Loading works
          <div className="dashboard-loading-top">
            <div className="spinner"></div>
            <p>{t.loading}</p>
          </div>
        ) : works.length === 0 ? (
          // No works available
          <div className="no-content-container">
            <img
              src={noWorkImage}
              alt="No Work Available"
              className="no-content-image"
            />
            <p className="no-content-message">{t.findingWorks}</p>
          </div>
        ) : (
          // Works list
          <div className="works-section">
            <h2 className="works-section-title">{t.availableWorks}</h2>
            <div className="works-list">
              {works.map((work) => (
                <WorkCard key={work._id} work={work} lang={lang} />
              ))}
              {/* Infinite scroll trigger */}
              {hasMorePages && (
                <div ref={observerTarget} className="infinite-scroll-trigger">
                  {loadingWorks && (
                    <div className="loading-more-works">
                      <div className="spinner"></div>
                      <span>{t.loading}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {showFilter && profile && (
        <FilterModal
          isOpen={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={handleFilterApply}
          profileSubCategories={profile.sub_categories || []}
          profileWorkAmount={profile.work_amount}
          lang={lang}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="home" lang={lang} />
    </div>
  );
}
